import type { LocationPlace } from '@rocco/ui/location/types/location-place';

type LocationLevel =
    | 'suburb'
    | 'district'
    | 'city'
    | 'region'
    | 'state'
    | 'all';

/**
 * Format a location/place to a readable string for displaying to users.
 * @param place The place/location to format.
 * @returns The formatted location string.
 */
export function formatLocation(
    location: LocationPlace | null,
    level: LocationLevel = 'all'
): string {
    if (!location) {
        return '';
    }

    const {
        suburb,
        district,
        city,
        region,
        state,
        country,
        placeType,
        fullAddress,
        latitude,
    } = location;

    if (!suburb && !district && !city && !region && !state && !fullAddress) {
        return '';
    }

    // Early return for district type or when fullAddress should be used
    if (
        (placeType === 'district' || (!suburb && !district && !city)) &&
        fullAddress
    ) {
        return fullAddress;
    }

    if (level !== 'all') {
        const locationMap: Record<LocationLevel, string | undefined> = {
            suburb: suburb || district || city || region || state,
            district: district || city || region || state,
            city: city || region || state,
            region: region || state,
            state: state,
            all: suburb || district || city || region || state,
        };

        return locationMap[level] || '';
    }

    // Original logic for LocationLevel.All
    const segments = [
        suburb,
        ...(placeType === 'district' ? [district, city] : [city || district]),
        region,
        // Do not show state for New Zealand (North Island and South Island).
        country !== 'New Zealand' ? state : '',
    ].filter(Boolean);

    const locationString = [...new Set(segments)].join(', ');

    return latitude ? locationString : `${locationString} *`;
}

/**
 * Format a place type to display in the search filter dropdown options.
 */
export function formatPlaceType(placeType: LocationPlace['placeType']) {
    switch (placeType) {
        case 'address':
            return 'Address' as const;
        case 'postcode':
            return 'Postcode' as const;
        case 'suburb':
            return 'Suburb' as const;
        case 'district':
            return 'District' as const;
        case 'city':
            return 'City' as const;
        case 'region':
            return 'Region' as const;
        case 'state':
            return 'State' as const;
        case 'country':
            return 'Country' as const;
        default:
            return 'Other' as const;
    }
}
